import { useCallback, useEffect } from 'react';

import type { ContainerProps } from '#pagination/components/types/container.props';

import { usePaginationStoreSet } from '#pagination/context/hooks/set.hook';
import { useTotalPages } from '#pagination/hooks/total-pages.hook';

/**
 * Custom hook to configure and manage pagination container state and handlers.
 *
 * This hook synchronizes pagination state with a global store and provides
 * memoized callback handlers for pagination actions such as navigating to the
 * first, last, next, previous, or a specific page, as well as changing the page size.
 *
 * @param params - Pagination configuration parameters.
 * @param params.onPage - Callback invoked when the page changes.
 * @param params.onPageSize - Callback invoked when the page size changes.
 * @param params.page - The current page index (default: 1).
 * @param params.pageSize - The number of items per page (default: 10).
 * @param params.total - The total number of items (default: 0).
 *
 * @returns An object containing:
 * - `handleFirstPageClick`: Handler to navigate to the first page.
 * - `handleLastPageClick`: Handler to navigate to the last page.
 * - `handleNextPageClick`: Handler to navigate to the next page.
 * - `handlePageClick`: Handler to navigate to a specific page.
 * - `handlePageSizeChanged`: Handler to change the page size.
 * - `handlePreviousPageClick`: Handler to navigate to the previous page.
 * - `pageSize`: The current page size.
 * - `total`: The total number of items.
 */
export const useContainerConfiguration = ({
  onPage,
  onPageSize,
  page,
  pageSize,
  total,
}: Pick<ContainerProps, 'onPage' | 'onPageSize' | 'page' | 'pageSize' | 'total'>) => {
  const set = usePaginationStoreSet();

  useEffect(() => {
    set({ page, pageSize, total });
  }, [page, pageSize, set, total]);

  const totalPages = useTotalPages();

  const handleFirstPageClick = useCallback(() => {
    onPage?.({ page: 1, previousPageIndex: page, total });
  }, [onPage, page, total]);

  const handleLastPageClick = useCallback(() => {
    onPage?.({ page: totalPages, previousPageIndex: page, total });
  }, [onPage, page, total, totalPages]);

  const handleNextPageClick = useCallback(() => {
    onPage?.({ page: page + 1, previousPageIndex: page, total });
  }, [onPage, page, total]);

  const handlePageClick = useCallback(
    (pageIndex: number) => {
      onPage?.({ page: pageIndex, previousPageIndex: page, total });
    },
    [onPage, page, total]
  );

  const handlePageSizeChanged = useCallback(
    (newPageSize: number) => {
      onPageSize?.(newPageSize);
    },
    [onPageSize]
  );

  const handlePreviousPageClick = useCallback(() => {
    onPage?.({ page: page - 1, previousPageIndex: page, total });
  }, [onPage, page, total]);

  return {
    handleFirstPageClick,
    handleLastPageClick,
    handleNextPageClick,
    handlePageClick,
    handlePageSizeChanged,
    handlePreviousPageClick,
    pageSize,
    total,
  };
};
